import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { X, ExternalLink, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

/**
 * Ad Trigger Context
 * 
 * Provides global ad triggering capabilities for pre-action ads,
 * before-logout ads, after-tool-completion ads, etc.
 * 
 * Usage:
 * const { triggerPreActionAd, triggerLogoutAd, triggerToolCompletionAd } = useAdTriggerContext();
 * 
 * // Before a button click
 * const handleCreateResume = async () => {
 *   await triggerPreActionAd('/resume/new', () => navigate('/resume/new'));
 * };
 * 
 * // Before logout
 * const handleLogout = async () => {
 *   await triggerLogoutAd(() => actualLogout());
 * };
 */

const AdTriggerContext = createContext(null);

export const useAdTriggerContext = () => {
  const context = useContext(AdTriggerContext);
  if (!context) {
    throw new Error('useAdTriggerContext must be used within AdTriggerProvider');
  }
  return context;
};

export const AdTriggerProvider = ({ children }) => {
  const [adData, setAdData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [pendingCallback, setPendingCallback] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const getDeviceType = () => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  };

  const getSessionId = () => {
    let sessionId = sessionStorage.getItem('ad_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('ad_session_id', sessionId);
    }
    return sessionId;
  };

  const getReferralSource = () => {
    const referrer = document.referrer;
    if (!referrer) return 'direct';
    
    try {
      const url = new URL(referrer);
      const host = url.hostname.toLowerCase();
      
      if (host.includes('google')) return 'google';
      if (host.includes('facebook') || host.includes('fb.com')) return 'facebook';
      if (host.includes('linkedin')) return 'linkedin';
      if (host.includes('twitter') || host.includes('x.com')) return 'twitter';
      if (host.includes('instagram')) return 'instagram';
      if (host.includes('youtube')) return 'youtube';
      if (host.includes('tiktok')) return 'tiktok';
      if (host === window.location.hostname) return 'internal';
      return 'other';
    } catch {
      return 'unknown';
    }
  };

  const recordEvent = useCallback(async (eventType, ad) => {
    if (!ad) return;
    
    try {
      await api.post('/advertisements/event', {
        campaign_id: ad.campaign_id,
        creative_id: ad.creative_id,
        placement_zone: ad.zone || 'pre_action',
        event_type: eventType,
        page_path: window.location.pathname,
        device: getDeviceType(),
        user_id: JSON.parse(localStorage.getItem('user') || 'null')?.id,
        session_id: getSessionId(),
        referrer: document.referrer,
        metadata: {
          referral_source: getReferralSource()
        }
      });
    } catch (error) {
      console.error('Error recording ad event:', error);
    }
  }, []);

  // Countdown timer for skip button
  useEffect(() => {
    if (isVisible && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, countdown]);

  const fetchAndShowAd = useCallback(async (triggerType, pagePath, callback, zone = 'pre_action') => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');

      const response = await api.post('/advertisements/render', {
        page_path: pagePath,
        zones: [zone],
        device: getDeviceType(),
        user_id: user?.id,
        user_role: user?.role,
        session_id: getSessionId(),
        trigger_type: triggerType,
        referrer: document.referrer
      });

      if (response.data.success && response.data.ads[zone]) {
        const ad = response.data.ads[zone];
        setAdData({ ...ad, zone, triggerType });
        setIsVisible(true);
        setPendingCallback(() => callback);
        setCountdown(3); // 3 second countdown before skip
        
        // Record impression
        recordEvent('impression', { ...ad, zone });
        
        return true;
      }
      
      // No ad to show, execute callback immediately
      if (callback) callback();
      return false;
      
    } catch (error) {
      console.error('Error fetching ad:', error);
      if (callback) callback();
      return false;
    }
  }, [recordEvent]);

  // Pre-action ad - shown before a user action (button click, link navigation)
  const triggerPreActionAd = useCallback(async (pagePath, callback) => {
    return fetchAndShowAd('before_click', pagePath, callback, 'pre_action');
  }, [fetchAndShowAd]);

  // Before logout ad - shown when user tries to logout
  const triggerLogoutAd = useCallback(async (callback) => {
    return fetchAndShowAd('before_logout', '/logout', callback, 'modal_popup');
  }, [fetchAndShowAd]);

  // Before submit ad - shown before form submission
  const triggerSubmitAd = useCallback(async (pagePath, callback) => {
    return fetchAndShowAd('before_submit', pagePath, callback, 'pre_action');
  }, [fetchAndShowAd]);

  // After tool completion ad - shown after using a tool
  const triggerToolCompletionAd = useCallback(async (toolName, callback) => {
    return fetchAndShowAd('after_tool_completion', `/tools/${toolName}`, callback, 'modal_popup');
  }, [fetchAndShowAd]);

  // On confirmation page - shown on confirmation/success pages
  const triggerConfirmationAd = useCallback(async (pagePath) => {
    return fetchAndShowAd('on_confirmation', pagePath, null, 'inline_content');
  }, [fetchAndShowAd]);

  // On thank you page - shown on thank you pages
  const triggerThankYouAd = useCallback(async (pagePath) => {
    return fetchAndShowAd('on_thank_you', pagePath, null, 'inline_content');
  }, [fetchAndShowAd]);

  const closeAd = useCallback((executePending = true) => {
    setIsVisible(false);
    setAdData(null);
    setCountdown(0);
    
    if (executePending && pendingCallback) {
      setTimeout(() => pendingCallback(), 100);
    }
    setPendingCallback(null);
  }, [pendingCallback]);

  const handleCtaClick = useCallback(() => {
    if (adData) {
      recordEvent('click', adData);
      
      const ctaUrl = adData.creative?.cta_url;
      if (ctaUrl) {
        if (ctaUrl.startsWith('http')) {
          window.open(ctaUrl, '_blank');
        } else {
          window.location.href = ctaUrl;
        }
      }
    }
    closeAd(false); // Don't execute pending after CTA click
  }, [adData, recordEvent, closeAd]);

  const skipAd = useCallback(() => {
    closeAd(true); // Execute pending action after skipping
  }, [closeAd]);

  const contextValue = {
    triggerPreActionAd,
    triggerLogoutAd,
    triggerSubmitAd,
    triggerToolCompletionAd,
    triggerConfirmationAd,
    triggerThankYouAd,
    isAdVisible: isVisible,
    currentAd: adData
  };

  // Get colors from creative
  const primaryColor = adData?.creative?.brand_colors?.primary || '#6366f1';
  const secondaryColor = adData?.creative?.brand_colors?.secondary || '#ec4899';

  return (
    <AdTriggerContext.Provider value={contextValue}>
      {children}
      
      {/* Pre-action / Modal Ad Overlay */}
      {isVisible && adData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header gradient bar */}
            <div 
              className="h-2"
              style={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}
            />
            
            {/* Sponsored label - positioned at top with proper spacing */}
            <div className="px-4 pt-3 pb-2 flex items-center justify-between">
              <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                Sponsored
              </span>
              {/* Close/Skip button */}
              <button
                onClick={skipAd}
                disabled={countdown > 0}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {countdown > 0 ? (
                  <>
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-500 mr-1">{countdown}</span>
                  </>
                ) : (
                  <X className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>
            
            {/* Ad Image */}
            {adData.creative?.image_url && (
              <img 
                src={adData.creative.image_url} 
                alt={adData.creative.title || 'Special Offer'}
                className="w-full h-44 object-cover"
              />
            )}
            
            {/* Ad Content */}
            <div className="p-6">
              {/* Pre-action label */}
              {adData.triggerType === 'before_click' && (
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium mb-3">
                  <span>⚡</span> Before you go...
                </div>
              )}
              
              {adData.triggerType === 'before_logout' && (
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-3">
                  <span>👋</span> Wait! Don&apos;t miss out
                </div>
              )}
              
              {adData.triggerType === 'after_tool_completion' && (
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium mb-3">
                  <span>🎉</span> Great job!
                </div>
              )}
              
              {adData.creative?.title && (
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {adData.creative.title}
                </h3>
              )}
              
              {adData.creative?.subtitle && (
                <p className="text-gray-600 mb-2">{adData.creative.subtitle}</p>
              )}
              
              {adData.creative?.body && (
                <p className="text-sm text-gray-500 mb-4">{adData.creative.body}</p>
              )}
              
              {/* Action buttons */}
              <div className="flex gap-3">
                {adData.creative?.cta_text && (
                  <Button
                    onClick={handleCtaClick}
                    className="flex-1"
                    style={{ 
                      background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` 
                    }}
                  >
                    {adData.creative.cta_text}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}
                
                {pendingCallback && (
                  <Button
                    onClick={skipAd}
                    variant="outline"
                    disabled={countdown > 0}
                    className="flex-1"
                  >
                    {countdown > 0 ? `Continue in ${countdown}s` : 'Continue'}
                  </Button>
                )}
              </div>
              
              {adData.creative?.disclaimer && (
                <p className="text-xs text-gray-400 mt-4 text-center">
                  {adData.creative.disclaimer}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </AdTriggerContext.Provider>
  );
};

export default AdTriggerProvider;
